/**
 * WorkspaceOverview — the rich dashboard shown as the default sub-tab.
 * Recent activity feed · recent candidates · recent employers · open roles · stage funnel.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Sparkles, Briefcase, Building2, Users, ArrowRight, Target, Trophy, Bell } from "lucide-react";
import {
  BASE, authHeader, StagePill, RoleStatusPill, SectionHeader, relTime, CANDIDATE_STAGES,
} from "./shared";
import { LiveBoard } from "../AdminLiveBoard";

export default function WorkspaceOverview({ pw, stats, onOpenCandidate, onOpenEmployer }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [scoreboard, setScoreboard] = useState(null);
  const [followUps, setFollowUps] = useState(null);
  const H = useMemo(() => authHeader(pw), [pw]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/dashboard`, H);
      setData(r.data);
      setLoaded(true);
    } catch (err) { console.error("WorkspaceOverview: dashboard load failed", err); }
    // Hunter panels — parallel, best-effort
    Promise.allSettled([
      axios.get(`${BASE}/hunter-scoreboard?days=7`, H).then(r => setScoreboard(r.data)),
      axios.get(`${BASE}/hunter/follow-ups`, H).then(r => setFollowUps(r.data)),
    ]);
  }, [H]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="workspace-overview">
      {/* ── Live board · active employers with roles open right now ── */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7" data-testid="dashboard-live-board">
        <LiveBoard pw={pw} onOpenEmployer={onOpenEmployer} compact />
      </div>

      {/* ── Hunter Cadence · who to call next ── */}
      {followUps && (followUps.counts.overdue + followUps.counts.due_today + followUps.counts.due_this_week) > 0 && (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7" data-testid="hunter-followups-panel">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="h-5 w-5 text-[#0D2F4D]"/>
            <SectionHeader title="Hunter cadence · who to call next" />
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className={`rounded-xl border p-4 ${followUps.counts.overdue > 0 ? "border-red-300 bg-red-50" : "border-[#E4E7EA] bg-[#FAFAF7]"}`} data-testid="followup-count-overdue">
              <p className={`font-display text-3xl font-extrabold ${followUps.counts.overdue > 0 ? "text-red-600" : "text-[#94A3B8]"}`}>{followUps.counts.overdue}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-[#6F8FA8] mt-1">Overdue</p>
            </div>
            <div className="rounded-xl border border-[#C6D21F]/40 bg-[#F7F9E8] p-4" data-testid="followup-count-today">
              <p className="font-display text-3xl font-extrabold text-[#0D2F4D]">{followUps.counts.due_today}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-[#6F8FA8] mt-1">Due today</p>
            </div>
            <div className="rounded-xl border border-[#E4E7EA] bg-[#FAFAF7] p-4" data-testid="followup-count-week">
              <p className="font-display text-3xl font-extrabold text-[#0D2F4D]">{followUps.counts.due_this_week}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-[#6F8FA8] mt-1">Due this week</p>
            </div>
          </div>
          <div className="space-y-1 max-h-[280px] overflow-y-auto">
            {[...followUps.overdue, ...followUps.due_today, ...followUps.due_this_week].slice(0, 12).map(r => (
              <button
                key={r.employer_id}
                onClick={() => onOpenEmployer?.(r.employer_id)}
                data-testid={`followup-row-${r.employer_id}`}
                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#F7F9E8] transition-colors"
              >
                <span className={`inline-block w-2 h-2 rounded-full ${r.days_from_today < 0 ? "bg-red-500" : r.days_from_today === 0 ? "bg-[#C6D21F]" : "bg-[#94A3B8]"}`}/>
                <span className="text-sm font-semibold text-[#0D2F4D] flex-1 truncate">{r.company_name}</span>
                <span className="text-xs text-[#6F8FA8] whitespace-nowrap">{r.area || "—"}</span>
                <span className="text-xs text-[#6F8FA8] whitespace-nowrap w-32 truncate">{r.assigned_to ? r.assigned_to.split("@")[0] : "unassigned"}</span>
                <span className={`text-xs font-mono ${r.days_from_today < 0 ? "text-red-600 font-bold" : "text-[#0D2F4D]"}`}>
                  {r.days_from_today < 0 ? `${Math.abs(r.days_from_today)}d overdue` : r.days_from_today === 0 ? "today" : `in ${r.days_from_today}d`}
                </span>
                <ArrowRight size={12} className="text-[#94A3B8]"/>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Hunter Scoreboard · team performance ── */}
      {scoreboard && scoreboard.rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7" data-testid="hunter-scoreboard-panel">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-[#0D2F4D]"/>
            <SectionHeader title="Hunter scoreboard · last 7 days" />
            <span className="ml-auto text-xs text-[#6F8FA8]">
              {scoreboard.totals.sends} sends · {scoreboard.totals.replies} replies · {scoreboard.totals.accounts} accounts
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F2F2EF] text-[10px] uppercase tracking-widest text-[#6F8FA8]">
                <tr>
                  <th className="text-left px-4 py-2">Rep</th>
                  <th className="text-right px-4 py-2">Sends</th>
                  <th className="text-right px-4 py-2">Replies</th>
                  <th className="text-right px-4 py-2">Reply %</th>
                  <th className="text-right px-4 py-2">Accounts</th>
                  <th className="text-right px-4 py-2">Stalled</th>
                </tr>
              </thead>
              <tbody data-testid="hunter-scoreboard-table">
                {scoreboard.rows.map((r, i) => (
                  <tr key={r.assigned_to} className="border-t border-[#F2F2EF]" data-testid={`hunter-row-${i}`}>
                    <td className="px-4 py-2">
                      <p className="font-semibold text-[#0D2F4D] capitalize">{r.display_name}</p>
                      <p className="text-[10px] text-[#6F8FA8]">{r.assigned_to === "unassigned" ? "—" : r.assigned_to}</p>
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-[#0D2F4D]">{r.sends}</td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-700">{r.replies}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      <span className={r.reply_rate >= 20 ? "text-emerald-700 font-bold" : r.reply_rate >= 5 ? "text-[#0D2F4D]" : "text-[#94A3B8]"}>{r.reply_rate}%</span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-[#0D2F4D]">{r.accounts}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      <span className={r.stalled > 10 ? "text-red-600 font-bold" : "text-[#0D2F4D]"}>{r.stalled}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stage funnel */}
      {stats?.candidates?.by_stage && (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7">
          <SectionHeader title="Candidate funnel" />
          <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
            {CANDIDATE_STAGES.map((s) => (
              <div
                key={s.v}
                data-testid={`funnel-${s.v}`}
                className="rounded-xl border p-4 text-center"
                style={{ borderColor: `${s.color}55`, background: `${s.color}0a` }}
              >
                <p className="font-display text-3xl font-extrabold" style={{ color: s.color }}>
                  {stats.candidates.by_stage[s.v] ?? 0}
                </p>
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mt-1.5">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats?.candidates?.top_industries?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7">
          <SectionHeader title="Top industries" />
          <ul className="space-y-3">
            {stats.candidates.top_industries.map((i) => {
              const max = Math.max(...stats.candidates.top_industries.map((x) => x.count));
              const pct = Math.round((i.count / max) * 100);
              return (
                <li key={i.industry} className="flex items-center gap-3">
                  <span className="text-[15px] text-[#0D2F4D] capitalize w-44 truncate">{i.industry}</span>
                  <div className="flex-1 h-2.5 bg-[#F2F2EF] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C6D21F]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-[#6F8FA8] w-8 text-right">{i.count}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent activity */}
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7">
          <SectionHeader title="Recent activity" />
          {!data?.recent_activity?.length ? (
            <p className="text-sm text-[#6F8FA8]">No activity yet.</p>
          ) : (
            <ol className="space-y-4">
              {data.recent_activity.slice(0, 8).map((e) => {
                const verb = (e.event_type || "").replaceAll("_", " ");
                const kind = (e.entity_type || "").replace(/s$/, "");
                const display = e.entity_display || kind || "record";
                return (
                  <li key={e.id} className="text-sm flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#C6D21F] mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[#0D2F4D] leading-snug">
                        <span className="font-semibold">{display}</span>
                        <span className="text-[#6F8FA8]"> · {kind} {verb}</span>
                      </p>
                      <p className="text-xs text-[#94A3B8] mt-0.5">{relTime(e.created_at)}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Open roles */}
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7">
          <SectionHeader title="Open roles" />
          {!data?.open_roles?.length ? (
            <p className="text-sm text-[#6F8FA8]">No open roles.</p>
          ) : (
            <ul className="space-y-3">
              {data.open_roles.map((r) => (
                <li key={r.id} className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-semibold text-[#0D2F4D] truncate">{r.title}</p>
                    <p className="text-xs text-[#6F8FA8] mt-0.5">{r.company?.name || "—"} · {r.location || "—"}</p>
                  </div>
                  <RoleStatusPill status={r.status} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent candidates */}
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7">
          <SectionHeader title="Recent candidates" />
          {!loaded ? (
            <p className="text-sm text-[#6F8FA8]">Loading…</p>
          ) : !data?.recent_candidates?.length ? (
            <p className="text-sm text-[#6F8FA8]">No candidates yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.recent_candidates.map((c) => (
                <li
                  key={c.id}
                  data-testid={`overview-candidate-${c.id}`}
                  className="flex items-center justify-between gap-3 cursor-pointer hover:bg-[#FAFAF7] p-2 -mx-2 rounded-lg"
                  onClick={() => onOpenCandidate?.(c.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-semibold text-[#0D2F4D]">
                      {c.person?.first_name} {c.person?.last_name}
                    </p>
                    <p className="text-xs text-[#6F8FA8] mt-0.5">{c.person?.email} · {c.industry || "—"}</p>
                  </div>
                  <StagePill stage={c.stage || "applied"} />
                  <ArrowRight size={12} className="text-[#94A3B8]" />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent employers */}
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7">
          <SectionHeader title="Recent employers" />
          {!loaded ? (
            <p className="text-sm text-[#6F8FA8]">Loading…</p>
          ) : !data?.recent_employers?.length ? (
            <p className="text-sm text-[#6F8FA8]">No employers yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.recent_employers.map((e) => (
                <li
                  key={e.id}
                  data-testid={`overview-employer-${e.id}`}
                  className="flex items-center justify-between gap-3 cursor-pointer hover:bg-[#FAFAF7] p-2 -mx-2 rounded-lg"
                  onClick={() => onOpenEmployer?.(e.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0D2F4D]">{e.company?.name}</p>
                    <p className="text-xs text-[#6F8FA8]">{e.industry || "—"} · {e.location || "—"}</p>
                  </div>
                  <ArrowRight size={12} className="text-[#94A3B8]" />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* All employers signed up — full directory shown at the end of the
          Careers overview so Cara/Laura can see the whole book at a glance. */}
      {data?.all_employers && (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-7" data-testid="overview-all-employers">
          <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
            <div>
              <SectionHeader title="All employers signed up" />
              <p className="text-xs text-[#6F8FA8] -mt-2">
                Every company we've onboarded, most recent first. Click any row to open the profile.
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D] bg-[#C6D21F] px-3 py-1 rounded-full">
              {data.all_employers.length} employer{data.all_employers.length === 1 ? "" : "s"}
            </span>
          </div>
          {data.all_employers.length === 0 ? (
            <p className="text-sm text-[#6F8FA8] text-center py-8">No employers on the books yet.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-[#F2F2EF]">
              <table className="w-full text-[15px]">
                <thead className="bg-[#F8F9FB] text-[10px] uppercase tracking-widest text-[#6F8FA8]">
                  <tr>
                    <th className="text-left px-4 py-3">Company</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">Contact</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Industry · Location</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Hiring for</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.all_employers.map((e) => (
                    <tr
                      key={e.id}
                      onClick={() => onOpenEmployer?.(e.id)}
                      className="border-t border-[#F2F2EF] hover:bg-[#FAFAF7] cursor-pointer transition-colors"
                      data-testid={`overview-all-employer-${e.id}`}
                    >
                      <td className="px-4 py-4">
                        <p className="font-semibold text-[#0D2F4D]">{e.company?.name || "—"}</p>
                        {e.company?.domain && (
                          <p className="text-xs text-[#6F8FA8] mt-0.5">{e.company.domain}</p>
                        )}
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        {e.contact ? (
                          <>
                            <p className="text-[#0D2F4D]">
                              {e.contact.first_name} {e.contact.last_name}
                            </p>
                            <p className="text-xs text-[#6F8FA8] mt-0.5">{e.contact.email}</p>
                          </>
                        ) : (
                          <p className="text-xs text-[#94A3B8]">No contact linked</p>
                        )}
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell text-[#0D2F4D]">
                        <p className="capitalize">{e.industry || "—"}</p>
                        <p className="text-xs text-[#6F8FA8] mt-0.5">{e.location || "—"}</p>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {(e.hiring_for || []).length ? (
                          <div className="flex flex-wrap gap-1">
                            {(e.hiring_for || []).slice(0, 3).map((h) => (
                              <span key={h} className="text-[10px] font-bold uppercase tracking-wide bg-[#F2F2EF] text-[#0D2F4D] px-2 py-0.5 rounded-full">
                                {h}
                              </span>
                            ))}
                            {(e.hiring_for || []).length > 3 && (
                              <span className="text-[10px] text-[#6F8FA8]">+{e.hiring_for.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <ArrowRight size={14} className="text-[#94A3B8] inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
